import React, { useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Link, useLocation } from "react-router-dom";
import {
  isPrinterUserType,
  PHYSICAL_ORDERS_PATH,
} from "../auth/session";

type Props = {
  isLoggedIn: boolean;
  userLabel: string;
  userType?: string | null;
  onLogout: () => void;
};

type NavItem = {
  label: string;
  to: string;
  activePaths?: string[];
};

const navItems: NavItem[] = [
  { label: "Home", to: "/" },
  { label: "Users", to: "/users" },
  { label: "Artworks", to: "/art" },
  {
    label: "Insights",
    to: "/insights",
    activePaths: ["/createInterview", "/interview"],
  },
  { label: "Invoice", to: "/invoice" },
  { label: "Charts", to: "/charts" },
  // { label: "Screens", to: "/screens" },
  { label: "Editorial", to: "/createEditorial", activePaths: ["/article"] },
  { label: "Collections", to: "/collections" },
  { label: "Physical", to: "/physical" },
  { label: "Physical Orders", to: "/physicalOrders" },
];

const Navbar = ({ isLoggedIn, userLabel, userType, onLogout }: Props) => {
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const normalizedPath = useMemo(
    () => (pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname),
    [pathname],
  );

  const isPathActive = (path: string) => {
    const normalizedTarget = path !== "/" ? path.replace(/\/+$/, "") : path;
    if (normalizedTarget === "/") {
      return normalizedPath === "/";
    }

    return (
      normalizedPath === normalizedTarget ||
      normalizedPath.startsWith(`${normalizedTarget}/`)
    );
  };

  const isNavItemActive = (item: NavItem) => {
    if (isPathActive(item.to)) {
      return true;
    }

    return item.activePaths?.some((path) => isPathActive(path)) ?? false;
  };

  const visibleNavItems = useMemo(
    () =>
      isPrinterUserType(userType)
        ? navItems.filter((item) => item.to === PHYSICAL_ORDERS_PATH)
        : navItems,
    [userType],
  );

  const handleLogout = () => {
    onLogout();
    setIsMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(12, 20, 31, 0.96)",
          borderBottom: "1px solid rgba(126, 153, 189, 0.28)",
          backdropFilter: "blur(10px)",
          overflowX: "clip",
        }}
      >
        <Toolbar
          sx={{
            minHeight: 72,
            width: "100%",
            maxWidth: 1920,
            boxSizing: "border-box",
            mx: "auto",
            px: {
              xs: 2,
              md: 3,
            },
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              minWidth: 0,
              flexShrink: 1,
              maxWidth: "100%",
            }}
          >
            <Box
              component="img"
              src="/ninfaLogo.png"
              alt="Ninfa logo"
              sx={{
                width: 36,
                height: 36,
                objectFit: "contain",
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: "#f5f9ff",
                  fontWeight: 800,
                  lineHeight: 1.1,
                  fontSize: {
                    xs: "1.05rem",
                    md: "1.15rem",
                  },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                ninfa admin
              </Typography>
              <Typography
                sx={{
                  display: {
                    xs: "none",
                    sm: "block",
                  },
                  color: "rgba(214, 232, 255, 0.74)",
                  fontSize: "0.72rem",
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Welcome, {userLabel}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              display: {
                xs: "none",
                lg: "flex",
              },
              ml: 1,
              mr: "auto",
              minWidth: 0,
              maxWidth: "100%",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            {visibleNavItems.map((item) => {
              const active = isNavItemActive(item);

              return (
                <Button
                  key={item.to}
                  component={Link}
                  to={item.to}
                  color="inherit"
                  sx={{
                    textTransform: "none",
                    color: active ? "#f5f9ff" : "rgba(214, 232, 255, 0.84)",
                    borderRadius: 2,
                    px: 1.4,
                    border: active
                      ? "1px solid rgba(114, 221, 247, 0.42)"
                      : "1px solid transparent",
                    backgroundColor: active
                      ? "rgba(114, 221, 247, 0.18)"
                      : "transparent",
                    whiteSpace: "nowrap",
                    fontWeight: active ? 700 : 600,
                    "&:hover": {
                      backgroundColor: "rgba(114, 221, 247, 0.14)",
                      color: "#f5f9ff",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexShrink: 0,
              ml: {
                xs: "auto",
                lg: 0,
              },
            }}
          >
            <Button
              onClick={handleLogout}
              startIcon={<LogoutRoundedIcon />}
              sx={{
                display: {
                  xs: "none",
                  lg: "inline-flex",
                },
                textTransform: "none",
                borderRadius: 2,
                color: "#ffe4e4",
                backgroundColor: "rgba(244, 67, 54, 0.16)",
                border: "1px solid rgba(244, 67, 54, 0.4)",
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.24)",
                },
              }}
            >
              Logout
            </Button>

            <IconButton
              onClick={() => setIsMobileMenuOpen((previous) => !previous)}
              sx={{
                display: {
                  xs: "inline-flex",
                  lg: "none",
                },
                color: "#f5f9ff",
                border: "1px solid rgba(126, 153, 189, 0.45)",
                backgroundColor: "rgba(114, 221, 247, 0.1)",
              }}
              aria-label={
                isMobileMenuOpen ? "Close navigation" : "Open navigation"
              }
            >
              <MenuRoundedIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={isMobileMenuOpen}
        onClose={closeMobileMenu}
        ModalProps={{
          hideBackdrop: true,
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "min(86vw, 340px)",
              sm: 340,
            },
            backgroundColor: "rgba(10, 16, 25, 0.99)",
            borderLeft: "1px solid rgba(126, 153, 189, 0.28)",
            color: "#f5f9ff",
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            height: "100%",
          }}
        >
          <Box sx={{ p: 2.5, pb: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{ fontWeight: 800, fontSize: "1.25rem", color: "#f5f9ff" }}
              >
                Navigation
              </Typography>
              <IconButton
                onClick={closeMobileMenu}
                sx={{
                  color: "#f5f9ff",
                  border: "1px solid rgba(126, 153, 189, 0.45)",
                  backgroundColor: "rgba(114, 221, 247, 0.1)",
                }}
                aria-label="Close navigation"
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
            <Typography
              sx={{
                color: "rgba(214, 232, 255, 0.72)",
                mt: 0.9,
                fontSize: "0.9rem",
              }}
            >
              Signed in as {userLabel}
            </Typography>
          </Box>

          <Box sx={{ overflowY: "auto", px: 1.5, pb: 2 }}>
            <List sx={{ py: 0 }}>
              {visibleNavItems.map((item) => {
                const active = isNavItemActive(item);

                return (
                  <ListItemButton
                    key={item.to}
                    component={Link}
                    to={item.to}
                    onClick={closeMobileMenu}
                    sx={{
                      borderRadius: 2,
                      mb: 0.75,
                      border: active
                        ? "1px solid rgba(114, 221, 247, 0.45)"
                        : "1px solid rgba(126, 153, 189, 0.18)",
                      backgroundColor: active
                        ? "rgba(114, 221, 247, 0.2)"
                        : "rgba(114, 221, 247, 0.08)",
                      "&:hover": {
                        backgroundColor: "rgba(114, 221, 247, 0.16)",
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: active ? 700 : 600,
                        color: active ? "#f5f9ff" : "#e6f2ff",
                      }}
                    />
                    <ChevronRightRoundedIcon
                      sx={{
                        color: active ? "#f5f9ff" : "rgba(214, 232, 255, 0.7)",
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          <Box sx={{ p: 2.5, pt: 1.5 }}>
            <Divider sx={{ borderColor: "rgba(126, 153, 189, 0.24)", mb: 2 }} />
            <Button
              onClick={handleLogout}
              fullWidth
              startIcon={<LogoutRoundedIcon />}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                color: "#ffe4e4",
                backgroundColor: "rgba(244, 67, 54, 0.16)",
                border: "1px solid rgba(244, 67, 54, 0.4)",
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.24)",
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
